import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, envPath, type CreatedProject, type Developer, type HostingView, type IssuedKey, type ProjectSummary } from '../api';
import { KeyReveal } from '../components/KeyReveal';
import { BalanceChip } from '../components/BalanceChip';
import { ModeSwitch, OldPasskeyNotice } from '../components/ModeSwitch';
import { PrepaidBand } from '../components/PrepaidBand';
import { ErrorState, LanguageSwitch, Logo, PageHeader, Spinner } from '../components/ui';
import { PRODUCT_NAME } from '../productName';
import { errorText, useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { useResource } from '../hooks';
import { useSession } from '../session';
import { readInviteStanding } from '../inviteCode';
import { isPrepaid402, prepaidBand, type PrepaidLike } from '../prepaidGate';
import { connectPollMs, firstTestUrl, HOME_VARIANT, mcpEndpoint, nextSetupStep, verifyPollMs, type HomeInput } from '../homeFlow';
import { HomeStart } from './HomeStart';
import { VerifyEmailBanner } from './AccountRecovery';
import { IncomingTransfers } from '../components/IncomingTransfers';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { CLI } from '../cliCommand';
import { GUIDE_PATH } from '../guidePage';
import { docsUrlFor } from '../docsLink';
import { slugSuggestionsFrom } from '../slugSuggestions';

/**
 * hideVerifyBanner（2026-09-28 creator/home-redesign）: ホームは「メールを確認する」を流れの最初の段として出す（homeFlow.ts）ので、
 * 上の帯は出さない（同じことを 2 か所で言わない）。ほかの画面は今までどおり帯を出す
 */
export function TopBar({ hideVerifyBanner = false }: { hideVerifyBanner?: boolean } = {}) {
  const { developer, signOut } = useSession();
  const { t } = useT();
  // スマホ幅のメニュー（2026-10-18 design/rauno-dashboard2・rauno-audit.md 上の帯（スマホ）の行）。
  // WHY: 以前はスマホでも全部を 1 行に並べ、使い方・お金・アカウントは名前の無いアイコン（?・財布・人）だけ・メールは隠れ・言語と Log out が
  //   帯の半分を取っていた。スマホは「残高 + メニュー 1 つ」にして、中に名前つきで並べる。PC（641px 以上）は前と同じ 1 行（styles.css .topbar-items）
  //   同じ部品を 1 つだけ描く（PC とスマホで 2 回描かない = 言語の切り替え・Log out が二重にならない）。開け閉めは見た目だけで、行き先・動きは前と同じ
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  // サイトのドキュメント（2026-10-26 console/docs-link）。ビルドの設定から作る = ドメインを書かない。値が無い・怪しいなら null = 出さない
  const docsUrl = docsUrlFor(import.meta.env.VITE_KUMO_SITE_URL);
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  return (
    <>
      <header className="topbar">
        <Link to="/" className="brand">
          <Logo size={26} />
          <span>{PRODUCT_NAME}</span>
          <span className="brand-sub">{t('common.brandSub')}</span>
        </Link>
        <div className="topbar-right">
          {/* Player / Creator の切り替え（旗 oneAccount のときだけ・components/ModeSwitch.tsx）。off なら何も描かない */}
          <ModeSwitch />
          {/* 今の残高（2026-10-07 console/usage-view・founder「どの画面でも残高が見える」）。押すと残高の画面へ。$5 未満は黄と "Low"（components/BalanceChip.tsx） */}
          <BalanceChip />
          <button
            type="button"
            className="topbar-menu-btn btn btn-ghost btn-sm"
            aria-expanded={menuOpen}
            aria-controls="topbar-items"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path d={menuOpen ? 'M6 6l12 12M18 6L6 18' : 'M4 7h16M4 12h16M4 17h16'} />
            </svg>
            <span>{t('common.menu')}</span>
          </button>
          <div id="topbar-items" className={`topbar-items ${menuOpen ? 'is-open' : ''}`}>
            {/* AI エージェントをつなぐ 1 行は使い方ガイドの始め方 1 にある（2026-10-07 founder「これは使用法に入れればよいよね」）= 入口は「How to use」1 つ */}
            {/* 使い方ガイド（2026-10-07 console/guide-page・founder「簡易的な 1 ページはほしい」）。「つなぐ」の隣 = 始め方の続きとして並べる。
                狭い画面では文字が隠れるので aria-label / title に同じ文を入れる（つなぐと同じ） */}
            <NavLink to={GUIDE_PATH} className={({ isActive }) => `topbar-link ${isActive ? 'is-active' : ''}`} aria-label={t('guide.topbarLink')} title={t('guide.topbarLink')}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14M12 17.5v.01" />
              </svg>
              <span>{t('guide.topbarLink')}</span>
            </NavLink>
            {/* サイトのドキュメント（2026-10-26 founder「上のバーに Docs のリンクを足して」）。ガイドの隣 = 「中の短い案内」と「サイトの詳しい説明」を並べる。
                サイトの別ページなので新しいタブで開く（作業中のダッシュボードを閉じない）。アイコンは紙（ガイドの ? と見分ける） */}
            {docsUrl && (
              <a href={docsUrl} target="_blank" rel="noopener noreferrer" className="topbar-link" aria-label={t('guide.docsLink')} title={t('guide.docsLink')}>
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  <path d="M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6" />
                </svg>
                <span>{t('guide.docsLink')}</span>
              </a>
            )}
            {/* お金（売上・出金・前払い残高）はゲームをまたぐので、どの画面からも 1 クリックで行けるようにトップバーに置く。
                641〜840px ではアイコンだけになる（styles.css）ので aria-label / title に名前を入れる（2026-10-26 console/docs-link） */}
            <NavLink to="/money" className={({ isActive }) => `topbar-link ${isActive ? 'is-active' : ''}`} aria-label={t('money.topbarLink')} title={t('money.topbarLink')}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path d="M3 7h18v12H3zM3 7l3-3h12l3 3M16 13h2" />
              </svg>
              <span>{t('money.topbarLink')}</span>
            </NavLink>
            {/* アカウントの設定（閉鎖）への入口。メールアドレス = 「自分のアカウント」なので、そこをリンクにする */}
            {/* 狭い画面ではメールが隠れる（styles.css .topbar-email）ので、人のアイコンを残して入口を消さない */}
            <NavLink to="/account" className={({ isActive }) => `topbar-link topbar-account ${isActive ? 'is-active' : ''}`} aria-label={t('account.topbarLink')} title={t('account.topbarLink')}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
              </svg>
              <span className="topbar-email">{developer?.email}</span>
            </NavLink>
            <LanguageSwitch />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
              {t('common.logOut')}
            </button>
          </div>
        </div>
      </header>
      {/* メール未確認の帯（確認済み・古いサーバーでは何も出ない）。0013 identity。ホームでは流れの段で出すので出さない */}
      {!hideVerifyBanner && <VerifyEmailBanner />}
      {/* 今のログイン用パスキーで入った作り手に「アカウントのページで登録し直して」（旗 oneAccount のときだけ・このタブで 1 回） */}
      <OldPasskeyNotice />
      {/* 閉鎖済み（出金のためにログインしてきた人）。新しいゲームは作れないことを、作ろうとする前に見せる */}
      {developer?.closedAt && (
        <div className="verify-banner" role="status">
          {/* 出金（payouts）を閉じている間は「出金はできます」を言わない（2026-09-30 console/words-pass） */}
          <span>{t(isLaunchHidden('payouts') ? 'account.closed.bannerLaunch' : 'account.closed.banner')}</span>
          <Link to="/account" className="btn btn-ghost btn-sm">
            {t('account.topbarLink')}
          </Link>
        </div>
      )}
    </>
  );
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,56}[a-z0-9])$/;

export function ProjectsPage() {
  const projects = useResource<{ projects: ProjectSummary[] }>('/v1/projects');
  const { developer, updateDeveloper } = useSession();
  // ホームの出し分け（homeFlow.ts）に使う 3 つ。どれも失敗しても画面は出す（分からない = 段を「まだ」として見せるだけ）
  //   前払い: 帯（PrepaidBand）にも同じものを渡す = 同じ GET を 2 本走らせない。30 秒ごと = 別タブで入金したら段が進む
  const prepaid = useResource<PrepaidLike & { currency?: string }>('/v1/money/prepaid', 30_000);
  //   Claude Code がつながっているか = 生きている委任トークン（kdt_）があるか。未確認の人は作れない（403）ので読まない
  //   つながるのを待つ間は 5 秒ごとに読み直す（homeFlow.ts connectPollMs の WHY・2026-10-14 gtm/onboarding）。間隔は前回の結果から決める
  //   （hook の順番は変えられないので state に置く。最初の 1 回は読み直しなし → 結果を見て決める）
  const [connectPoll, setConnectPoll] = useState<number | null>(null);
  const delegations = useResource<{ delegations?: unknown[] }>(
    developer?.emailVerified === false ? null : '/v1/developers/delegations',
    connectPoll ?? undefined
  );
  //   メールの確認を待つ間は自分の情報を 10 秒ごとに読み直す（別のタブで確認のリンクを開いても、このタブが進むように・homeFlow.ts verifyPollMs）。
  //   確認済みになったらセッションに反映 → 上の委任の読み込みが始まり、ホームの段が「つなぐ」へ進む
  const me = useResource<{ developer: Developer }>(developer?.emailVerified === false ? '/v1/developers/me' : null, verifyPollMs(developer?.emailVerified) ?? undefined);
  //   エージェントをつなぐ 1 行の URL（mcpUrl・Core 2026-09-28）。無い・形が怪しい → ガイドへのリンク（homeFlow.ts mcpEndpoint = オリジンだけなら /mcp を足す）
  const platform = useResource<{ mcpUrl?: unknown }>('/v1/platform/info');
  const connectedNow = delegations.data ? (delegations.data.delegations?.length ?? 0) > 0 : null;
  const nextConnectPoll = connectPollMs({ emailVerified: developer?.emailVerified, connected: connectedNow });
  useEffect(() => {
    setConnectPoll(nextConnectPoll);
  }, [nextConnectPoll]);
  const verifiedNow = me.data?.developer.emailVerified === true;
  useEffect(() => {
    if (!verifiedNow) return;
    updateDeveloper({ emailVerified: true });
    // 招待クレジットは確認で入る（invitePending → 入った）。30 秒を待たずに残高の段・帯を直す
    prepaid.reload();
  }, [verifiedNow, updateDeveloper, prepaid.reload]);
  //   最初のテスト版のカード（homeFlow.ts firstTestUrl・2026-10-15 gtm/onboarding）: いちばん新しいプロジェクト（一覧は新しい順）の
  //   テスト版と本番の公開の情報を読む。テスト版がまだ動いていない間は 15 秒ごとに読み直す（AI が公開した瞬間にホームが変わる）。
  //   動いたら読み直しを止める（本番は「みんなに公開」の確認のあとで変わる = 1 回読めば足りる。ページを開き直せば読み直す）
  const newestId = projects.data?.projects[0]?.id ?? null;
  const [testPoll, setTestPoll] = useState<number | null>(null);
  const devHosting = useResource<HostingView>(newestId ? `${envPath(newestId, 'development')}/hosting` : null, testPoll ?? undefined);
  const prodHosting = useResource<HostingView>(newestId ? `${envPath(newestId, 'production')}/hosting` : null);
  const testLive = !!devHosting.data?.active;
  useEffect(() => {
    setTestPoll(newestId && !testLive ? 15_000 : null);
  }, [newestId, testLive]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedProject | null>(null);
  const navigate = useNavigate();
  const { t, tr } = useT();

  if (created) {
    const keys: IssuedKey[] = created.environments.flatMap((e) => e.keys);
    return (
      <div className="page-wrap">
        <TopBar />
        <main className="container narrow" id="main">
          <PageHeader eyebrow={t('projects.createdEyebrow')} title={created.name}>
            <span className="pill">{created.slug}</span>
          </PageHeader>
          {created.secretKeysWithheld && (
            <EmailUnverifiedNotice>{tr('projects.secretKeysWithheld', { cmd: <code>{CLI} keys create --secret</code> })}</EmailUnverifiedNotice>
          )}
          <KeyReveal keys={keys} />
          <div className="reveal-done">
            <button type="button" className="btn btn-primary" onClick={() => navigate(`/p/${created.id}/development/overview`)}>
              {t('projects.savedOpen')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  const list = projects.data?.projects ?? [];
  const flow: HomeInput = {
    emailVerified: developer?.emailVerified,
    prepaid: prepaidBand(prepaid.data),
    // 招待クレジットの額（確認待ちなら確認の段が「確認すると {amount} が入ります」と言う・homeFlow.ts inviteVerifyAmount）
    invite: (() => {
      const inv = readInviteStanding(prepaid.data?.invite);
      return inv ? { granted: inv.granted, currency: prepaid.data?.currency ?? 'usd' } : null;
    })(),
    connected: connectedNow,
    projectCount: list.length
  };
  const setupStep = nextSetupStep(flow);
  const createForm = creating ? <CreateProject onCancel={() => setCreating(false)} onCreated={setCreated} /> : null;
  // ホーム（HomeStart.tsx）は、プロジェクトの一覧が読めたときだけ出す。読めないとき（失敗・読み込み中）は今までどおり上の帯で案内する
  const home = !!projects.data;
  return (
    <div className="page-wrap">
      <TopBar hideVerifyBanner={home && developer?.emailVerified === false} />
      {/* B・C はゲームがあっても入金を入力欄の下の段で出す（homeFlow.ts setupNotices）。止まっている警告（使い切った）は帯で出す。まだ入金していない（start）はホームの流れの段で出すので帯にしない（2026-09-28 home-redesign） */}
      <PrepaidBand resource={prepaid} hideStart={home && (list.length === 0 || HOME_VARIANT !== 'a' || setupStep === 'topUp')} />
      <main className="container home-container" id="main">
        {projects.loading && !projects.data && <Spinner />}
        {projects.error && <ErrorState error={projects.error} onRetry={projects.reload} />}
        {/* あなたあてのプロジェクトの受け渡し（2026-10-21 core/handoff-1a）。無ければ何も出さない */}
        <IncomingTransfers />
        {home && (
          <HomeStart
            projects={list}
            flow={flow}
            mcpUrl={mcpEndpoint(platform.data?.mcpUrl)}
            firstTest={(() => {
              const url = firstTestUrl(devHosting.data, prodHosting.data);
              return url && list[0] ? { name: list[0].name, url } : null;
            })()}
            creating={creating}
            onCreate={() => setCreating(true)}
            createForm={createForm}
          />
        )}
      </main>
    </div>
  );
}

function CreateProject({ onCancel, onCreated }: { onCancel: () => void; onCreated: (p: CreatedProject) => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  // 402（前払い残高が足りずプロジェクトを作れない）なら入金ページへの道を出す（FunctionsPage と同じ。文言だけでは次に何をするか分からない）。
  // balance_due だけでなく 402 全般（prepaidGate.ts: Core が「0 以下で止める」を入れるときにコードが増えうる）
  const [due, setDue] = useState(false);
  // 409 slug_taken / 400 slug_reserved（予約語）のときにサーバーが返す空いているスラッグ（slugSuggestions.ts の WHY）。押すと欄に入るだけで、作成は本人が押す
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const { t } = useT();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDue(false);
    setSuggestions([]);
    if (slug && !SLUG_RE.test(slug)) {
      setError(t('projects.form.slugInvalid'));
      return;
    }
    setBusy(true);
    try {
      const r = await api<{ project: CreatedProject; secretKeysWithheld?: boolean }>('/v1/projects', { method: 'POST', body: { name, ...(slug ? { slug } : {}) } });
      // secretKeysWithheld = メールが未確認で sk_ が作られなかった（2026-09-28 Core SEC-DEVLOGIN-01）。作成の画面で案内する
      onCreated({ ...r.project, secretKeysWithheld: r.secretKeysWithheld === true });
    } catch (err) {
      setError(errorText(err as ApiError));
      setDue(isPrepaid402(err as ApiError));
      setSuggestions(slugSuggestionsFrom(err as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card create-card" onSubmit={submit}>
      <h2 className="card-title">{t('projects.form.title')}</h2>
      <div className="form-row">
        <label className="field">
          <span>{t('projects.form.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} autoFocus placeholder={t('projects.form.namePlaceholder')} />
        </label>
        <label className="field">
          <span>
            {t('projects.form.slug')}
            <small>{t('projects.form.slugNote')}</small>
          </span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="booking-app"
            pattern="[a-z0-9][a-z0-9-]{1,56}[a-z0-9]"
            aria-describedby="slug-hint"
          />
          <small id="slug-hint" className="muted">
            {t('projects.form.slugHint')}
          </small>
        </label>
      </div>
      {error && (
        <div className="alert alert-error" role="alert">
          <span>{error}</span>
          {due && (
            <Link to="/money/prepaid" className="btn btn-sm btn-ghost">
              {t('pp.topUp')}
            </Link>
          )}
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="slug-suggestions">
          <span className="small muted" id="slug-suggestions-label">
            {t('projects.form.slugSuggestions')}
          </span>
          <ul className="chips" aria-labelledby="slug-suggestions-label">
            {suggestions.map((s) => (
              <li key={s}>
                {/* type="button" = 押しても送信しない（フォームの中なので既定は submit）。選んだ候補は aria-pressed で分かるように */}
                <button type="button" className="chip chip-button mono" aria-pressed={slug === s} onClick={() => setSlug(s)}>
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" disabled={busy || !name.trim()}>
          {busy ? t('projects.form.creating') : t('projects.form.submit')}
        </button>
      </div>
    </form>
  );
}
